import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Lock, Shield, Menu, X } from 'lucide-react';
import bquestLogo from '@/assets/bquest-logo.png';
import { Button } from '@/shared/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet';
import { useGuestSignupModal } from '../context/GuestSignupModalContext';
import { useUnauthRumAction } from '../hooks/useUnauthRumAction';
import { PreLoginSidebar } from './PreLoginSidebar';
import { TABS, type LockedTab, type PublicTab } from './preLoginTabs';

function CreateAccountBanner() {
  const { openModal } = useGuestSignupModal();
  const trackAction = useUnauthRumAction();
  const triggerModal = () => {
    trackAction('unauth.banner_click');
    openModal({ trigger: 'banner_cta' });
  };
  return (
    <button
      type="button"
      onClick={triggerModal}
      className="w-full bg-[#f0faf7] border-b border-[#c8e6df] hover:bg-[#e8f5f0] transition-colors cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#026857]"
      aria-label="Create an account for full access"
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-[#026857]">
          <Shield className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Create an account for full access</strong>
            <span className="hidden sm:inline text-[#026857]/70"> · Save providers · Message directly · Track your care requests</span>
          </span>
        </div>
        <span
          className="bg-[#026857] text-white font-bold whitespace-nowrap flex-shrink-0 inline-flex items-center justify-center rounded-md text-sm h-9 px-3"
        >
          Create free account →
        </span>
      </div>
    </button>
  );
}

/**
 * Slide-out navigation for guests below the `lg` breakpoint — mirrors the
 * authenticated `MobileMenu` (Topbar) so the pre-login shell has the same
 * responsive behavior. Public tabs navigate + close; locked tabs pop the
 * signup modal (same handlers as PreLoginSidebar).
 */
function PreLoginMobileMenu({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const { openModal } = useGuestSignupModal();
  const trackAction = useUnauthRumAction();

  const handlePublicClick = (tab: PublicTab) => {
    trackAction('unauth.tab_view', { tab: tab.label });
    onClose();
  };

  const handleLockedClick = (tab: LockedTab) => {
    trackAction('unauth.locked_tab_click', { tab: tab.label });
    openModal({ trigger: 'locked_tab_click', triggerEntityId: tab.label });
    onClose();
  };

  const handleCreateAccount = () => {
    trackAction('unauth.banner_click');
    openModal({ trigger: 'banner_cta' });
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-80 p-0" showCloseButton={false}>
        <SheetHeader className="border-b p-6 bg-gradient-to-br from-[#026857] to-[#378F94]">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white text-lg">Menu</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </SheetHeader>

        <nav className="flex flex-col p-4 gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            if ('locked' in tab) {
              return (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => handleLockedClick(tab)}
                  className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-gray-400 hover:bg-gray-100 transition-colors text-left"
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{tab.label}</span>
                  <Lock className="w-4 h-4" />
                </button>
              );
            }
            const active = location.pathname === tab.href;
            return (
              <Link
                key={tab.label}
                to={tab.href}
                onClick={() => handlePublicClick(tab)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  active ? 'bg-[#026857]/10 text-[#026857]' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{tab.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <Button
            type="button"
            onClick={handleCreateAccount}
            className="w-full bg-[#026857] hover:bg-[#025246] text-white font-bold"
          >
            Create free account
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export const PreLoginShell = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return (
    <>
      {/* Desktop (lg+) guest navigation; the topbar below only exists under lg,
          so sticky page elements clear --app-topbar-h (same contract as AppLayout). */}
      <PreLoginSidebar />
      <div className="min-h-screen bg-background [--app-topbar-h:4rem] lg:pl-60 lg:[--app-topbar-h:0px]">
        <header className="bg-[#026857] border-b border-[#025246] sticky top-0 z-50 lg:hidden">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to="/" className="hover:opacity-80 transition-opacity">
                  <div className="flex items-center gap-2">
                    <img src={bquestLogo} alt="bQuest" className="h-7 brightness-0 invert" />
                    <span className="text-sm text-white/60">|</span>
                    <span className="text-xs text-white whitespace-nowrap">Home</span>
                  </div>
                </Link>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to="/login"
                  className="text-sm font-termina font-bold uppercase text-white/90 hover:text-white hover:underline underline-offset-4 px-3 py-2 min-h-[44px] flex items-center"
                >
                  Login
                </Link>

                {/* Mobile Menu Button (mirrors Topbar) */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/10 text-white"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        <CreateAccountBanner />

        <PreLoginMobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

        <main>
          <Outlet />
        </main>
      </div>
    </>
  );
};
